import { ActionTree } from 'vuex';
import { state as authState } from '../auth/auth';
import { CateringPackage, CateringPackageItem, CateringPackagesResponse, CateringRecommendationRequest } from '@/types/cateringBot';
import { CateringBotState, RootState } from '../types';

export const actions: ActionTree<CateringBotState, RootState> = {
	async fetchAvailability({ commit }): Promise<void> {
		try {
			const response = await authState.axiosInstance.post<{ enabled: boolean }>(`${process.env.VUE_APP_API_PREFIX}/rally-intelligence/availability`, { bot: 'catering_bot' });
			commit('SET_ENABLED', response.data.enabled);
		}
		catch {
			// Availability check failures fail closed: feature stays hidden.
			commit('SET_ENABLED', false);
		}
	},

	async getRecommendations({ commit }, input: CateringRecommendationRequest): Promise<void> {
		const response = await authState.axiosInstance.post<CateringPackagesResponse>(`${process.env.VUE_APP_API_PREFIX}/rally-intelligence/catering-bot/guest/generate-package`, input);
		// Credits only burn on a successful response, not on network/API failures.
		commit('rallyIntelligence/DECREMENT_CREDITS', null, { root: true });
		commit('SET_RESULTS', { input, results: response.data.packages ?? [] });
	},

	/**
	 * Refine a previously recommended package using the user's natural-language input.
	 *
	 * @param {{ index: number; refinementText: string }} payload - target package index and the refinement text from the user
	 * @return {Promise<void>}
	 */
	async refinePackage({ state, commit }, payload: { index: number; refinementText: string }): Promise<void> {
		if (!state.lastResults) {
			return;
		}
		const priorPackage: CateringPackage = state.lastResults[payload.index];
		commit('SET_REFINING_INDEX', payload.index);
		try {
			const response = await authState.axiosInstance.post<CateringPackagesResponse>(`${process.env.VUE_APP_API_PREFIX}/rally-intelligence/catering-bot/guest/refine-package`, {
				prior_package: priorPackage,
				refinement_text: payload.refinementText,
				dietary_preferences: state.lastInput?.dietary_preferences,
				allergens: state.lastInput?.allergens
			});
			// Credits only burn on a successful response, not on network/API failures.
			commit('rallyIntelligence/DECREMENT_CREDITS', null, { root: true });
			const refined = response.data.packages?.[0];
			if (refined) {
				commit('PREPEND_REFINEMENT', refined);
			}
		}
		finally {
			commit('SET_REFINING_INDEX', null);
		}
	},

	async addPackageItemToCart({ dispatch }, item: CateringPackageItem): Promise<void> {
		const normalizedOptions = (item.options ?? []).map(group => ({
			...group,
			optionGroupType: group.type
		}));
		const lastOrderedOptions = normalizedOptions.flatMap(group =>
			(group.values ?? []).map(v => ({ id: v.id, sku: v.sku, name: v.name, quantity: v.quantity ?? 1 }))
		);
		const tempItem = { ...item, options: normalizedOptions, lastOrderedOptions };
		dispatch('cart/setTemporaryItem', { tempItem, quantity: tempItem.quantity }, { root: true });
		await dispatch('cart/addToCart', { originalPrice: tempItem.price }, { root: true });
	}
};
